import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import ActivityTag from './components/activityTag';
import CompareAndCollect from './components/compareAndCollect';
import CopyProductButton from './components/copyProductButton';
import DatasheetButton from './components/datasheetButton';
import MultiLineText from './components/multiLineText';
import PicturePerview from './components/picturePerview';
import PriceLevelAndDiscount from './components/priceLevelAndDiscount';
import ProductTags from './components/productTags';
import PurchaseButton from './components/purchaseButton';
import PurchaseNumInputs from './components/purchaseNumInputs';
import StockAndDeliver from './components/stockAndDeliver';
import TotalPrice from './components/totalPrice';

import LabelText from '@/components/LabelText';
import SvgIcon from '@/components/SvgIcon';
import { LayoutContext } from '@/layouts/baseLayout/provider';
import { ProductItemContext } from '@/providers/productItem';
import { ProductListContext } from '@/providers/productList';
import { goItemBuriedPoint, buriedPoint } from '@/utils/buriedPoint';
import Utils from '@/utils/Utils';

function MainItem(props) {
  const { resourceData, onlySScreen } = useContext(LayoutContext);
  const { showStockBtn, track_zone, searchResult } = useContext(ProductListContext);
  const { marketLabelVO, index, rs_log } = useContext(ProductItemContext);

  const {
    productVO,
    paramLinkedMap, // 动态参数
    isMro, // 是否为mro
    lightCatalogName, // 类目（高亮）
    lightProductModel = '', // 型号（高亮）
    lightProductCode, // 编号（高亮）
    lightBrandName, //品牌（高亮）
    lightStandard,
    lightProductIntro, // 描述（高亮）
    remarkPrefixTitle,
    lightProductName,
    isFavorite, // 是否收藏
    showMoreBtn,
    showMoreChannel,
    showDiscountSuite,
    onShowMoreChannel,
    onShowDiscountSuite,
    totalStockNumber,
    showProductSpuMapBtn,
    showProductSpuMap,
    onShowProductSpuMap,
    showProductConnectorMapBtn,
    showProductConnectorMap,
    onShowProductConnectorMap,
  } = props;

  const [purchaseCount, setPurchaseCount] = useState(0);
  const [useInputCount, setUseInputCount] = useState('');
  const purchaseInputRef = useRef(null);
  // 单价
  const [price, setPrice] = useState(0);
  const [showNewIcon, setShowNewIcon] = useState(false);

  const totalPrices = useMemo(() => {
    return Math.round(price * purchaseCount * 100) / 100;
  }, [price, purchaseCount]);

  useEffect(() => {
    if (
      !Utils.getLocal('product-item-btn-guide') &&
      index === 0 &&
      (showProductSpuMapBtn || showProductConnectorMapBtn)
    ) {
      setShowNewIcon(true);
      Utils.setLocal('product-item-btn-guide', true);
    }
  }, [showProductSpuMapBtn, showProductConnectorMapBtn, index]);

  // 近期成交
  const recentlySalesCountDom = useMemo(() => {
    const count = productVO?.recentlySalesCount || 0;
    if (productVO?.productUnsealVendition === 'no' || productVO?.selfProductCode || count === 0) {
      return;
    }
    const list = [];
    if (marketLabelVO?.isHaveBought) {
      list.push('我买过');
    }
    if (count <= 100) {
      list.push(`近期成交${count}单`);
    } else {
      list.push(`近期成交100单+`);
    }
    return <p className="text-[#69788A]">{list.join(' · ')}</p>;
  }, [productVO, marketLabelVO]);

  const luceneBreviaryImageUrlList = useMemo(() => {
    return productVO?.luceneBreviaryImageUrls?.split('<$>') || [];
  }, [productVO]);

  // 动态参数
  const paramLinkedDom = useMemo(() => {
    const max = isMro ? 2 : 4;
    return (
      paramLinkedMap &&
      Object.entries(paramLinkedMap)
        ?.filter(([, value]) => value && value != '-')
        .slice(0, max)
        .map(([key, value]) => {
          const label = key;
          return <LabelText key={key} label={label} text={value} title={value} bp="参数" showLabelTitle></LabelText>;
        })
    );
  }, [paramLinkedMap, isMro]);

  // 描述
  const productInfoDom = useMemo(() => {
    const paramLen = paramLinkedDom?.length;
    if (lightProductIntro || remarkPrefixTitle) {
      return (
        <MultiLineText
          label="描述"
          text={lightProductIntro || remarkPrefixTitle}
          title={lightProductIntro || remarkPrefixTitle}
          line={paramLen > 3 ? 1 : 2}
        ></MultiLineText>
      );
    }
  }, [lightProductIntro, remarkPrefixTitle]);

  // 跳转详情链接
  const detailPage = useMemo(() => {
    let url = `${resourceData?.projectConfigVO?.lcscItemUrl}/${isMro ? 'mro/' : ''}${productVO.productId}.html`;
    if (track_zone) {
      const splitor = url.includes('?') ? '&' : '?';
      url += `${splitor}fromZone=${Utils.safeEncodeURI(track_zone)}`;
    }
    return url;
  }, [isMro, resourceData, productVO, track_zone]);

  // 更多渠道
  const moreChannelDom = useMemo(() => {
    if (!showMoreBtn) {
      return;
    }
    return (
      <button
        className="mr-[10px] h-[26px] w-[95px] rounded-[13px] bg-[#F5F6F9] text-[#1FA0E8] hover:bg-[#ECEEF0]"
        onClick={clickShowMore}
      >
        <span className="mr-[7px]">更多渠道</span>
        {showMoreChannel ? (
          <span className="mb-[-2px] ml-[px] inline-block size-[7px] rotate-[45deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
        ) : (
          <span className="mb-[3px] ml-[px] inline-block size-[7px] rotate-[225deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
        )}
      </button>
    );
  }, [showMoreChannel, showMoreBtn]);

  // 优惠套装
  const taoDom = useMemo(() => {
    if (productVO.productStockStatus === 'yes' && productVO.isPromotionDiscount) {
      return (
        <button
          className="mr-[10px] h-[26px] w-[95px] rounded-[13px] bg-[#F5F6F9] text-[#1FA0E8] hover:bg-[#ECEEF0]"
          onClick={clickShowDiscountSuite}
        >
          <span className="mr-[7px]">优惠套装</span>
          {showDiscountSuite ? (
            <span className="mb-[-2px] ml-[px] inline-block size-[7px] rotate-[45deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          ) : (
            <span className="mb-[3px] ml-[px] inline-block size-[7px] rotate-[225deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          )}
        </button>
      );
    }
  }, [showDiscountSuite]);

  // 包装替代
  const productSpuMapDom = useMemo(() => {
    if (!showProductSpuMapBtn) {
      return;
    }
    return (
      <div className={'relative'}>
        <button
          className="mr-[10px] h-[26px] w-[95px] rounded-[13px] bg-[#F5F6F9] text-[#1FA0E8] hover:bg-[#ECEEF0]"
          onClick={clickShowProductSpuMap}
        >
          <span className="mr-[7px]">包装替代</span>
          {showProductSpuMap ? (
            <span className="mb-[-2px] ml-[px] inline-block size-[7px] rotate-[45deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          ) : (
            <span className="mb-[3px] ml-[px] inline-block size-[7px] rotate-[225deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          )}
        </button>
        {showNewIcon && (
          <SvgIcon app="so" iconName={'new'} className="absolute right-[-5px] top-[-10px] w-[35px] h-[14px]"></SvgIcon>
        )}
      </div>
    );
  }, [showProductSpuMap, showProductSpuMapBtn, showNewIcon]);

  // 连接器
  const connectProductDom = useMemo(() => {
    if (!showProductConnectorMapBtn) {
      return;
    }
    return (
      <div className={'relative'}>
        <button
          className="h-[26px] w-[95px] rounded-[13px] bg-[#F5F6F9] text-[#1FA0E8] hover:bg-[#ECEEF0]"
          onClick={clickShowProductConnectorMap}
        >
          <span className="mr-[7px]">配套连接器</span>
          {showProductConnectorMap ? (
            <span className="mb-[-2px] ml-[px] inline-block size-[7px] rotate-[45deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          ) : (
            <span className="mb-[3px] ml-[px] inline-block size-[7px] rotate-[225deg] border-l-[1px] border-t-[1px] border-solid border-[#0294e8]"></span>
          )}
        </button>
        {showNewIcon && (
          <SvgIcon app="so" iconName={'new'} className="absolute right-[-5px] top-[-10px] w-[35px] h-[14px]"></SvgIcon>
        )}
      </div>
    );
  }, [showProductConnectorMap, showProductConnectorMapBtn, showNewIcon]);

  function clickShowMore() {
    onShowMoreChannel(!showMoreChannel);
  }

  function clickShowDiscountSuite() {
    onShowDiscountSuite(!showDiscountSuite);
  }

  function clickShowProductSpuMap() {
    onShowProductSpuMap(!showProductSpuMap, 'operate');
  }

  function clickShowProductConnectorMap() {
    onShowProductConnectorMap(!showProductConnectorMap);
  }

  const stockBtnRef = useRef(null);

  const stockBtnDom = useMemo(() => {
    return (
      showStockBtn && (
        <button ref={stockBtnRef} data-spm="b" className="mt-[7px] text-[#1FA0E8]" onClick={gotoStock}>
          我要备货
        </button>
      )
    );
  }, [showStockBtn, productVO]);

  function gotoStock() {
    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html?type=stock`;
    if (productVO?.productModel) {
      url += `&stockModel=${encodeURI(encodeURI(productVO?.productModel))}`;
    }
    if (productVO?.productGradePlateName) {
      url += `&brandName=${encodeURI(encodeURI(productVO?.productGradePlateName))}`;
    }
    if (track_zone) {
      url += `&fromZone=${Utils.safeEncodeURI(track_zone)}`;
    }
    url = Utils.getBtnSpmQueryUrl(url, stockBtnRef.current);
    window.open(url, '_blank');
    commonBuried('PURCHASE_PLAN');
  }

  function gotoItem(kind) {
    goItemBuriedPoint({
      kind,
      pc: productVO?.productId,
      brand: productVO?.productGradePlateName,
      model: productVO?.productModel,
      code: productVO?.productCode,
      kw: searchResult?.globalSearchKeyword,
      cp: searchResult?.currePage,
      ix: index,
      track_zone: track_zone,
      type: 'mianProduct',
      rs_log,
    });
  }

  function commonBuried(op) {
    buriedPoint({
      op,
      data: {
        pc: productVO?.productId,
        brand: productVO?.productGradePlateName,
        model: productVO?.productModel,
        code: productVO?.productCode,
        cp: searchResult?.currePage,
        kw: searchResult?.globalSearchKeyword,
        ix: index,
        rs_log,
      },
    });
  }

  return (
    <div className={'flex bg-[white] p-[20px] leading-[18px]'}>
      <div>
        <div className="flex">
          <div className="w-[121px] h-[105px] select-none relative">
            <a
              aria-label="点击跳转详情"
              href={detailPage}
              data-spm="t"
              target="_blank"
              onClick={() => gotoItem('picture')}
              data-custom-data={JSON.stringify({
                productType: isMro ? 'mro' : 'electronic',
                productCode: productVO?.productCode,
              })}
            >
              <ActivityTag {...props}></ActivityTag>
              <PicturePerview
                src={productVO?.breviaryImageUrl}
                imageList={luceneBreviaryImageUrlList}
                alt={productVO?.productName}
                productId={productVO?.productId}
              />
            </a>
          </div>
          {isMro ? (
            <div className={Utils.addBigScreenClassName('w-[368px]', '_lg:w-[448px]', onlySScreen)}>
              <LabelText
                text={lightProductName || productVO?.productName}
                title={lightProductName || productVO?.productName}
                isTitle
                textBold
                isMro
                link={detailPage}
                spm="n"
                bp="型号"
                onClick={() => gotoItem('name')}
              ></LabelText>
              <div className="flex">
                <div className={Utils.addBigScreenClassName('w-[178px]', '_lg:w-[215px]', onlySScreen)}>
                  <LabelText
                    label="品牌"
                    text={lightBrandName || productVO?.productGradePlateName}
                    title={lightBrandName || productVO?.productGradePlateName}
                    link={`${resourceData?.projectConfigVO?.lcscListUrl}${isMro ? '/mro' : ''}/brand/${productVO?.productGradePlateId}.html`}
                    underline
                    spm="p"
                    onClick={() => commonBuried('go_brand')}
                  ></LabelText>
                  <LabelText
                    label="类目"
                    text={lightCatalogName || productVO?.productType}
                    title={lightCatalogName || productVO?.productType}
                    link={`${resourceData?.projectConfigVO?.lcscListUrl}/catalog/${productVO.productTypeCode}.html${isMro ? '?mro=1' : ''}`}
                    underline
                    spm="l"
                    onClick={() => commonBuried('go_catalog')}
                  ></LabelText>
                  <LabelText
                    label="型号"
                    text={lightProductModel || productVO?.productModel}
                    title={lightProductModel || productVO?.productModel}
                  ></LabelText>
                </div>
                <div className={Utils.addBigScreenClassName('w-[190px]', '_lg:w-[233px]', onlySScreen)}>
                  <LabelText
                    label="编号"
                    text={lightProductCode || productVO?.productCode}
                    title={lightProductCode || productVO?.productCode}
                  ></LabelText>
                  {paramLinkedDom}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={Utils.addBigScreenClassName('w-[178px]', '_lg:w-[215px]', onlySScreen)}>
                <LabelText
                  text={lightProductModel || productVO?.productModel}
                  title={lightProductModel || productVO?.productModel}
                  isTitle
                  textBold
                  link={detailPage}
                  spm="n"
                  bp="型号"
                  onClick={() => gotoItem('name')}
                ></LabelText>
                <LabelText
                  label="品牌"
                  text={lightBrandName || productVO?.productGradePlateName}
                  title={lightBrandName || productVO?.productGradePlateName}
                  link={`${resourceData?.projectConfigVO?.lcscListUrl}${isMro ? '/mro' : ''}/brand/${productVO?.productGradePlateId}.html`}
                  underline
                  spm="p"
                  onClick={() => commonBuried('go_brand')}
                ></LabelText>
                <LabelText
                  label="封装"
                  text={lightStandard || productVO?.encapsulationModel}
                  title={lightStandard || productVO?.encapsulationModel}
                ></LabelText>
                <LabelText
                  label="类目"
                  text={lightCatalogName || productVO?.productType}
                  title={lightCatalogName || productVO?.productType}
                  link={`${resourceData?.projectConfigVO?.lcscListUrl}/catalog/${productVO.productTypeCode}.html${isMro ? '?mro=1' : ''}`}
                  underline
                  spm="l"
                  onClick={() => commonBuried('go_catalog')}
                ></LabelText>
                <LabelText
                  label="编号"
                  text={lightProductCode || productVO?.productCode}
                  title={lightProductCode || productVO?.productCode}
                ></LabelText>
              </div>
              <div className={Utils.addBigScreenClassName('w-[190px]', '_lg:w-[233px]', onlySScreen)}>
                {paramLinkedDom}
                {productInfoDom}
              </div>
            </>
          )}
          <div className={Utils.addBigScreenClassName('w-[157px]', '_lg:w-[180px]', onlySScreen)}>
            {productVO?.productUnsealVendition !== 'no' && (
              <ProductTags {...props} totalPrices={totalPrices} purchaseCount={purchaseCount}></ProductTags>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center mt-[12px] flex-shrink _lg:w-[749px] w-[656px]">
          <CompareAndCollect isFavorite={isFavorite} productId={productVO?.productId} />
          <DatasheetButton {...props} />
          <CopyProductButton {...props} detailPage={detailPage} />
          {moreChannelDom}
          {taoDom}
          {productSpuMapDom}
          {connectProductDom}
        </div>
      </div>
      <div className="flex flex-1 flex-col items-center">
        {productVO?.productUnsealVendition !== 'no' &&
          (productVO?.isShowPrice === false ? (
            <>需询价</>
          ) : (
            <PriceLevelAndDiscount {...props} purchaseCount={purchaseCount} onChange={(value) => setPrice(value)} />
          ))}
      </div>
      <div className={Utils.addBigScreenClassName('w-[161px]', '_lg:w-[186px]', onlySScreen)}>
        {productVO?.productUnsealVendition !== 'no' && <StockAndDeliver {...props} />}
      </div>
      <div className={`${Utils.addBigScreenClassName('w-[120px]', '_lg:w-[140px]', onlySScreen)} text-right`}>
        {productVO?.productUnsealVendition !== 'no' && (
          <>
            <PurchaseNumInputs
              ref={purchaseInputRef}
              productVO={productVO}
              totalStockNumber={totalStockNumber}
              onChange={(value) => setPurchaseCount(value)}
              onChangeUseInputCount={(value) => setUseInputCount(value)}
            />
            <TotalPrice totalPrices={totalPrices} isShowPrice={productVO?.isShowPrice} />
          </>
        )}
        <PurchaseButton
          {...props}
          purchaseCount={purchaseCount}
          useInputCount={useInputCount}
          totalPrices={totalPrices}
          validate={purchaseInputRef?.current?.validate}
        />
        {recentlySalesCountDom}
        {stockBtnDom}
      </div>
    </div>
  );
}

export default MainItem;
